import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/react";
import {
  X,
  Loader,
  UserPlus,
  Trash2,
  Crown,
  User,
  ChevronDown,
  Check,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
  getUsers,
  getProjectById,
  shareProject,
  unshareProject,
} from "../../service/apiService";
import { User as UserType } from "../../types";

interface ShareProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  currentUserId: string;
  isOwner: boolean;
}

interface ProjectMember {
  user_id: string;
  email: string;
  role: "owner" | "member";
}

const getInitials = (email?: string, userId?: string): string => {
  const source = email || userId || "?";
  const parts = source.split(/[@._\s-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const getAvatarColor = (str: string): { bg: string; text: string } => {
  const colors = [
    { bg: "#CECBF6", text: "#3C3489" },
    { bg: "#9FE1CB", text: "#085041" },
    { bg: "#F5C4B3", text: "#712B13" },
    { bg: "#B5D4F4", text: "#0C447C" },
    { bg: "#F9D8A8", text: "#7A4A0A" },
    { bg: "#D4F1C0", text: "#2D6A0F" },
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const ShareProjectDialog: React.FC<ShareProjectDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  currentUserId,
  isOwner,
}) => {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const [allUsers, setAllUsers] = useState<UserType[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAllUsers();
      fetchMembers();
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedUser(null);
      setUserQuery("");
    }
  }, [isOpen]);

  const fetchAllUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const result = await getUsers();
      // Exclude current user from dropdown
      setAllUsers(
        result.filter(
          (u: UserType) =>
            u.id?.toLowerCase() !== currentUserId.toLowerCase()
        )
      );
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const project = await getProjectById(projectId);

      const memberList: ProjectMember[] = [];

      // Add owner first
      if (project.user_id) {
        memberList.push({
          user_id: project.user_id,
          email: project.user_id,
          role: "owner",
        });
      }

      // Add shared_with users as members
      const sharedWith: string[] = project.shared_with || [];
      sharedWith.forEach((userId: string) => {
        memberList.push({
          user_id: userId,
          email: userId,
          role: "member",
        });
      });

      setMembers(memberList);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to load team members");
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const filteredUsers =
    userQuery === ""
      ? allUsers //← Show ALL users when dropdown opens
      : allUsers.filter((user) =>
          user.id?.toLowerCase().includes(userQuery.toLowerCase())
        );

  const availableUsers = filteredUsers.filter(
    (user) =>
      !members.some(
        (m) => m.user_id?.toLowerCase() === user.id?.toLowerCase()
      )
  );

  const handleInvite = async () => {
    if (!selectedUser) {
      toast.error("Please select a user to invite");
      return;
    }

    const memberEmail = selectedUser.id.toLowerCase().trim();

    // Check duplicate
    const already = members.find(
      (m) => m.user_id?.toLowerCase() === memberEmail
    );
    if (already) {
      toast.error("This person is already a member");
      return;
    }

    setIsInviting(true);
    try {
      await shareProject(projectId, [memberEmail]);
      toast.success(`${memberEmail} added to project`);
      setSelectedUser(null);
      setUserQuery("");
      await fetchMembers();
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || "Failed to add member";
      toast.error(msg);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (
    memberId: string,
    memberEmail?: string
  ) => {
    setRemovingMemberId(memberId);
    try {
      await unshareProject(projectId, [memberId]);
      setMembers((prev) =>
        prev.filter((m) => m.user_id !== memberId)
      );
      toast.success(
        `${memberEmail || memberId} removed from project`
      );
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || "Failed to remove member";
      toast.error(msg);
    } finally {
      setRemovingMemberId(null);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>

        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 scrim" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-gray-700 w-full max-w-md overflow-hidden">

                {/* ── Header ── */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Share Project
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                      {projectName}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>

                <div className="px-6 py-4">

                  {/* ── Info banner ── */}
                  <div className="mb-4 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700leading-relaxed">
                    Team members can view all conversations in this project. Only the project owner or a super admin can add or remove members.
                  </div>

                  {/* ── Invite section (owner only) ── */}
                  {isOwner && (
                    <div className="mb-5">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Invite team member
                      </label>
                      <div className="flex gap-2">
                        <Combobox
                          value={selectedUser}
                          onChange={setSelectedUser}
                        >
                          <div className="relative flex-1">
                            <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left border border-gray-200 focus-within:ring-2 focus-within:ring-violet-400 focus-within:border-transparent sm:text-sm">
                              <ComboboxInput
                                className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-700 bg-white rounded-lg focus:outline-none"
                                displayValue={(user: UserType) =>
                                  user?.id || ""
                                }
                                onChange={(e) =>
                                  setUserQuery(e.target.value)
                                }
                                placeholder="Select or search user..."
                              />

                              {/* Loading spinner */}
                              {isLoadingUsers && (
                                <div className="absolute inset-y-0 right-6 flex items-center">
                                  <RefreshCcw
                                    size={14}
                                    className="animate-spin text-gray-400"
                                  />
                                </div>
                              )}<ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronDown
                                  size={16}
                                  className="text-gray-400"
                                />
                              </ComboboxButton>
                            </div>

                            {/* Dropdown ── */}
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setUserQuery("")}
                            >
                              <ComboboxOptions className="absolute mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-20">

                                {/* "Type to search users..." */}
                                {/* Now shows user list directly on open*/}

                                {availableUsers.length === 0 ? (
                                  <div className="relative cursor-default select-none px-4 py-2 text-gray-500 text-xs">
                                    {isLoadingUsers
                                      ? "Loading users..."
                                      : allUsers.length === 0
                ? "No users found."
                                      : "All users are already members."}
                                  </div>
                                ) : (
                                  availableUsers.map((user) => (
                                    <ComboboxOption
                                      key={user.id}
                                      value={user}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                          active
                                            ? "bg-violet-600 text-white"
                                            : "text-gray-900"
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span
                                            className={`block truncate text-sm ${
                                              selected
                                                ? "font-medium"
                                                : "font-normal"
                                            }`}
                                          >
                                            {user.id}
                                          </span>
                                          {selected && (
                                            <span
                                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                active
                                                  ? "text-white"
                                                  : "text-violet-600"
                                              }`}
                                            >
                                              <Check size={16} />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </ComboboxOption>
                                  ))
                                )}
                              </ComboboxOptions>
                            </Transition>
                          </div>
                        </Combobox>

                        {/* Invite button */}
                        <button
                          onClick={handleInvite}
                          disabled={isInviting || !selectedUser}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isInviting ? (
                <Loader size={14} className="animate-spin" />
                          ) : (
                            <UserPlus size={14} />
                          )}
                          Invite
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Members list ── */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Team members ({members.length})
                    </label>

                    {isLoadingMembers ? (
                      <div className="py-8 flex justify-center">
                        <Loader
                          className="animate-spin text-violet-600"
                          size={20}
                        />
                      </div>
                    ) : members.length === 0 ? (
                      <div className="py-4 text-center text-sm text-gray-400">
                        No members yet. Invite someone to collaborate.
                      </div>
                    ) : (
                      <div className="space-y-2max-h-56 overflow-y-auto pr-1">
                        {members.map((member) => {
                          const initials = getInitials(
                            member.email,
                            member.user_id
                          );
                          const colors = getAvatarColor(member.user_id);

                          const isCurrentUser =
                            member.user_id?.toLowerCase() ===
                            currentUserId.toLowerCase();

                          const isMemberOwner = member.role === "owner";

                          return (
                            <div
                              key={member.user_id}
                              className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group"
                            >
                              {/* Avatar */}
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 border-2 border-white shadow-sm select-none"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                }}
                              >
                                {initials}
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800truncate">
                                  {member.email || member.user_id}
                                {isCurrentUser && (
                                    <span className="ml-1 text-xs text-gray-400">
                                      (you)
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Role badge + remove button */}
                              <div className="flex items-center gap-2">
                                <span
                                  className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                                    isMemberOwner
                                      ? "bg-amber-50 text-amber-700 border border-amber-200"
                                      : "bg-gray-100 text-gray-500"
                                  }`}
                                >
                                  {isMemberOwner ? (
                                    <Crown size={10} />
                                  ) : (
                                    <User size={10} />
                                  )}
                                  {isMemberOwner ? "Owner" : "Member"}
                                </span>

                                {/* Remove button */}
                                {isOwner &&
                                  !isMemberOwner &&
                                  !isCurrentUser && (
                                    <button
                                      onClick={() =>
                                        handleRemoveMember(
                                          member.user_id,
                                          member.email
                                        )
                                      }
                                      disabled={
                                        removingMemberId === member.user_id
                                      }
                                      className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                                      title="Remove member"
                                    >
                                      {removingMemberId === member.user_id ? (
                                        <Loader
                                          size={12}
                                          className="animate-spin"
                                        />
                                      ) : (
                                        <Trash2 size={12} />
                                      )}
                                    </button>
                                  )}
                </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Footer ── */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Done
                  </button>
                </div></Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ShareProjectDialog;